import React, { useMemo } from "react";
import { Button, Dropdown, Space, Table, Tag, Tooltip } from "antd";
import {
  LinkOutlined,
  SettingOutlined,
  UsergroupAddOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import lib from "../../assets/utils/lib";
import { useAuth } from "../../assets/utils/useAuth";
import { canManageEncounterCodes } from "../compoinents/EnrolleeEncounterCodes";
import { getFacilityPlanType, getFacilityTypeName } from "../facilities/facilityUtils";
import {
  formatEnrolleeName,
  formatPaName,
  formatSearchDate,
  isActiveStatus,
} from "./searchResultUtils";

function StatusTag({ active, activeLabel = "Active", inactiveLabel = "Inactive" }) {
  return (
    <Tag color={active ? "success" : "error"}>
      {active ? activeLabel : inactiveLabel}
    </Tag>
  );
}

function buildActionsColumn(listTarget, onRowAction, { canIssueEncounterCode = false } = {}) {
  const open = (record, action) => ({
    onClick: (e) => {
      e.stopPropagation();
      onRowAction(record, action);
    },
  });

  const enrolleeExtraWidth = listTarget === "enrollees" && canIssueEncounterCode ? 40 : 0;

  return {
    title: "",
    key: "actions",
    fixed: "right",
    width: listTarget === "clients" ? 100 : 72 + enrolleeExtraWidth,
    render: (_, record) => (
      <Space size={0} onClick={(e) => e.stopPropagation()}>
        {listTarget === "clients" ? (
          <>
            <Tooltip title="Settings">
              <Button
                type="text"
                size="small"
                icon={<SettingOutlined />}
                onClick={open(record, listTarget).onClick}
              />
            </Tooltip>
            <Tooltip title="Enrollees">
              <Button
                type="text"
                size="small"
                icon={<UsergroupAddOutlined />}
                onClick={open(record, "client_enrollees").onClick}
              />
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip title="Open">
              <Button
                type="text"
                size="small"
                icon={
                  ["pa", "claims", "checkins", "onboarding", "appointments"].includes(listTarget)
                    ? <LinkOutlined />
                    : <SettingOutlined />
                }
                onClick={open(record, listTarget).onClick}
              />
            </Tooltip>
            {listTarget === "enrollees" &&
            canIssueEncounterCode &&
            isActiveStatus(record.status) ? (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "view",
                      label: "View / manage code",
                      icon: <SafetyCertificateOutlined />,
                      onClick: () => onRowAction(record, "enrollee_encounter_codes"),
                    },
                    {
                      key: "generate",
                      label: "Generate code",
                      icon: <SafetyCertificateOutlined />,
                      onClick: () => onRowAction(record, "enrollee_encounter_codes"),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Tooltip title="Check-in code">
                  <Button
                    type="text"
                    size="small"
                    icon={<SafetyCertificateOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Tooltip>
              </Dropdown>
            ) : null}
          </>
        )}
      </Space>
    ),
  };
}

function getColumnsForTarget(listTarget, onRowAction, options = {}) {
  const actions = buildActionsColumn(listTarget, onRowAction, options);

  switch (listTarget) {
    case "clients":
      return [
        {
          title: "Name",
          key: "name",
          dataIndex: "name",
          ellipsis: true,
          render: (v) => v?.toUpperCase?.() || v || "—",
        },
        { title: "Policy no", dataIndex: "policy_no", key: "policy_no", width: 120, ellipsis: true },
        { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
        {
          title: "Package",
          key: "package",
          width: 110,
          ellipsis: true,
          render: (_, r) => r?.insurance_package?.toUpperCase?.() || "—",
        },
        {
          title: "Type",
          key: "premium_type",
          width: 100,
          render: (_, r) => r?.premium_type?.toUpperCase?.() || "—",
        },
        {
          title: "Enrollees",
          key: "enrollees",
          width: 100,
          render: (_, r) => `${r?.active_enrollees ?? 0} / ${r?.enrolee_size ?? 0}`,
        },
        {
          title: "Status",
          key: "status",
          width: 96,
          render: (_, r) => <StatusTag active={isActiveStatus(r.status)} />,
        },
        actions,
      ];

    case "enrollees":
      return [
        {
          title: "Name",
          key: "name",
          ellipsis: true,
          render: (_, r) => formatEnrolleeName(r),
        },
        { title: "Insurance no", dataIndex: "insurance_no", key: "insurance_no", width: 130, ellipsis: true },
        { title: "Policy no", dataIndex: "policy_no", key: "policy_no", width: 120, ellipsis: true },
        { title: "Gender", dataIndex: "gender", key: "gender", width: 88 },
        { title: "Role", dataIndex: "role", key: "role", width: 100, ellipsis: true },
        {
          title: "Location",
          key: "location",
          width: 140,
          ellipsis: true,
          render: (_, r) => [r?.lga, r?.state].filter(Boolean).join(", ") || "—",
        },
        {
          title: "Status",
          key: "status",
          width: 96,
          render: (_, r) => <StatusTag active={isActiveStatus(r.status)} />,
        },
        actions,
      ];

    case "facilities":
      return [
        { title: "Facility", dataIndex: "facility_name", key: "facility_name", ellipsis: true },
        {
          title: "Type",
          key: "facility_type",
          width: 110,
          render: (_, r) => {
            const type = getFacilityTypeName(r);
            return type ? type.toUpperCase() : "—";
          },
        },
        {
          title: "Plan",
          key: "facility_plan_type",
          width: 110,
          render: (_, r) => {
            const plan = getFacilityPlanType(r);
            return plan ? plan.toUpperCase() : "—";
          },
        },
        { title: "Phone", dataIndex: "facility_phone", key: "facility_phone", width: 120 },
        {
          title: "Location",
          key: "location",
          width: 150,
          ellipsis: true,
          render: (_, r) => [r?.facility_city, r?.facility_state].filter(Boolean).join(" | ") || "—",
        },
        {
          title: "Status",
          key: "status",
          width: 96,
          render: (_, r) => <StatusTag active={isActiveStatus(r.facility_status)} />,
        },
        actions,
      ];

    case "corporations":
      return [
        { title: "Organization", dataIndex: "org_name", key: "org_name", ellipsis: true },
        {
          title: "Code",
          key: "code",
          width: 120,
          render: (_, r) => r?.org_social_no || r?.code || "—",
        },
        { title: "Email", dataIndex: "org_email", key: "org_email", ellipsis: true },
        {
          title: "Location",
          key: "location",
          width: 150,
          ellipsis: true,
          render: (_, r) => [r?.org_city, r?.org_state].filter(Boolean).join(" ") || "—",
        },
        {
          title: "Status",
          key: "status",
          width: 96,
          render: (_, r) => <StatusTag active={Boolean(r.org_status)} />,
        },
      ];

    case "constableItems":
      return [
        { title: "Item", dataIndex: "item_name", key: "item_name", ellipsis: true },
        { title: "Code", dataIndex: "item_code", key: "item_code", width: 110 },
        { title: "Category", dataIndex: "category_name", key: "category_name", width: 130, ellipsis: true },
        {
          title: "Brand",
          dataIndex: "item_brand",
          key: "item_brand",
          width: 120,
          ellipsis: true,
          render: (v, r) => (Number(r?.category_id) === 1 ? v || "—" : "—"),
        },
        {
          title: "Dosage",
          key: "dosage",
          width: 120,
          ellipsis: true,
          render: (_, r) =>
            Number(r?.category_id) === 1
              ? [r?.item_dosage, r?.item_strength].filter(Boolean).join(" · ") || "—"
              : "—",
        },
        {
          title: "Status",
          key: "status",
          width: 110,
          render: (_, r) => (
            <StatusTag active={Boolean(r.item_status)} activeLabel="Available" inactiveLabel="Unavailable" />
          ),
        },
      ];

    case "pa":
      return [
        { title: "Patient", key: "name", width: 140, render: (_, r) => formatPaName(r) },
        { title: "Check-in ID", dataIndex: "checkin_id", key: "checkin_id", width: 140, ellipsis: true },
        {
          title: "Date",
          key: "checkin_date",
          width: 150,
          render: (_, r) => formatSearchDate(r?.checkin_date),
        },
        { title: "Insurance no", dataIndex: "insurance_no", key: "insurance_no", width: 130, ellipsis: true },
        { title: "Facility", dataIndex: "facility_name", key: "facility_name", ellipsis: true },
        actions,
      ];

    case "claims":
      return [
        { title: "Patient", key: "name", width: 140, render: (_, r) => formatPaName(r) },
        { title: "Claims ID", dataIndex: "claims_id", key: "claims_id", width: 100 },
        {
          title: "Check-in",
          key: "checkin",
          width: 140,
          ellipsis: true,
          render: (_, r) => r?.checkin_code ?? r?.checkin_id ?? "—",
        },
        {
          title: "Date",
          key: "checkin_date",
          width: 150,
          render: (_, r) => formatSearchDate(r?.checkin_date),
        },
        { title: "Insurance no", dataIndex: "insurance_no", key: "insurance_no", width: 130, ellipsis: true },
        actions,
      ];

    case "checkins": {
      const consumerName = (r) => {
        const c = r?.consumer || {};
        return [c.first_name, c.surname].filter(Boolean).join(" ") || formatPaName(r);
      };
      return [
        { title: "Patient", key: "name", width: 140, render: (_, r) => consumerName(r) },
        { title: "Check-in ID", dataIndex: "checkin_id", key: "checkin_id", width: 140, ellipsis: true },
        {
          title: "Status",
          dataIndex: "aggregated_status",
          key: "aggregated_status",
          width: 110,
          render: (v) => <Tag>{v || "Pending"}</Tag>,
        },
        { title: "Facility", dataIndex: "facility_name", key: "facility_name", ellipsis: true },
        { title: "Plan", dataIndex: "plan_name", key: "plan_name", width: 120, ellipsis: true },
        {
          title: "Requested",
          key: "requested",
          width: 110,
          align: "right",
          render: (_, r) => {
            const v = r?.filed?.total_requested_cost;
            return v != null ? Number(v).toLocaleString() : "—";
          },
        },
        actions,
      ];
    }

    case "onboarding":
      return [
        { title: "Organization", dataIndex: "name", key: "name", ellipsis: true },
        { title: "Phone", dataIndex: "phone", key: "phone", width: 120 },
        { title: "Email", dataIndex: "email", key: "email", ellipsis: true },
        { title: "Org code", dataIndex: "social_no", key: "social_no", width: 120 },
        { title: "Package", dataIndex: "insurance_package", key: "insurance_package", width: 120, ellipsis: true },
        {
          title: "Status",
          key: "status",
          width: 96,
          render: (_, r) => (
            <StatusTag active={isActiveStatus(r?.status)} activeLabel="Active" inactiveLabel="Lead" />
          ),
        },
        actions,
      ];

    case "appointments":
      return [
        {
          title: "Patient",
          key: "patient",
          ellipsis: true,
          render: (_, r) =>
            r?.patient_name || [r?.first_name, r?.surname].filter(Boolean).join(" ") || "—",
        },
        {
          title: "Scheduled",
          key: "scheduled_start",
          width: 160,
          render: (_, r) => formatSearchDate(r?.scheduled_start),
        },
        { title: "Facility", dataIndex: "facility_name", key: "facility_name", ellipsis: true },
        { title: "Insurance no", dataIndex: "insurance_no", key: "insurance_no", width: 130, ellipsis: true },
        {
          title: "Status",
          dataIndex: "status",
          key: "status",
          width: 110,
          render: (v) => <Tag>{v || "scheduled"}</Tag>,
        },
        actions,
      ];

    default:
      return [
        {
          title: "Record",
          key: "record",
          render: (_, r) => r?.name || r?.id || JSON.stringify(r).slice(0, 80),
        },
        actions,
      ];
  }
}

export function SearchResultsTable({ listTarget, results, onRowAction }) {
  const { permissions } = useAuth();
  const canIssueEncounterCode = canManageEncounterCodes(permissions);
  const resource = lib.unifiedSearchTargetToResource(listTarget) || listTarget;

  const columns = useMemo(
    () => getColumnsForTarget(listTarget, onRowAction, { canIssueEncounterCode }),
    [listTarget, onRowAction, canIssueEncounterCode],
  );

  if (!Array.isArray(results) || results.length === 0) return null;

  const hasOpenAction = !["corporations", "constableItems"].includes(listTarget);

  return (
    <Table
      className="search-results-table"
      size="small"
      rowKey={(record, index) => lib.unifiedSearchRowKey(resource, record, index)}
      columns={columns}
      dataSource={results}
      scroll={{ x: "max-content" }}
      pagination={false}
      onRow={
        hasOpenAction
          ? (record) => ({
              onClick: () => onRowAction(record, listTarget),
              style: { cursor: "pointer" },
            })
          : undefined
      }
    />
  );
}
